import { IMAGE_FORMATS, ImageFormat, ConversionRoute } from '../types/media';

export const FORMAT_LABELS: Record<ImageFormat, string> = {
  jpeg: 'JPEG',
  png: 'PNG',
  webp: 'WebP',
  svg: 'SVG',
  gif: 'GIF',
  bmp: 'BMP',
  tiff: 'TIFF',
  ico: 'ICO',
};

export const FORMAT_MIME: Record<ImageFormat, string> = {
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  gif: 'image/gif',
  bmp: 'image/bmp',
  tiff: 'image/tiff',
  ico: 'image/x-icon',
};

/** File extensions accepted per format (lowercase, no dot) */
export const FORMAT_EXTENSIONS: Record<ImageFormat, string[]> = {
  jpeg: ['jpg', 'jpeg', 'jpe'],
  png: ['png'],
  webp: ['webp'],
  svg: ['svg'],
  gif: ['gif'],
  bmp: ['bmp'],
  tiff: ['tif', 'tiff'],
  ico: ['ico'],
};

export function isImageFormat(value: string): value is ImageFormat {
  return (IMAGE_FORMATS as readonly string[]).includes(value);
}

/** Matches backend `isConversionSupported` (raster → SVG is not supported). */
export function isConversionSupported(from: ImageFormat, to: ImageFormat): boolean {
  if (from === to) return true;
  if (to === 'svg') return false;
  return true;
}

export function buildConversionSlug(from: ImageFormat, to: ImageFormat): string {
  return `${from}-${to}`;
}

export function parseConversionSlug(slug: string): {
  from: ImageFormat;
  to: ImageFormat;
} | null {
  const normalized = slug.toLowerCase().trim();
  const dashIndex = normalized.indexOf('-');
  if (dashIndex <= 0) return null;

  const from = normalized.slice(0, dashIndex);
  const to = normalized.slice(dashIndex + 1);

  if (!isImageFormat(from) || !isImageFormat(to) || from === to) {
    return null;
  }

  if (!isConversionSupported(from, to)) {
    return null;
  }

  return { from, to };
}

export function getAllConversionRoutes(): ConversionRoute[] {
  const routes: ConversionRoute[] = [];

  for (const from of IMAGE_FORMATS) {
    for (const to of IMAGE_FORMATS) {
      if (from === to || !isConversionSupported(from, to)) continue;
      routes.push({
        slug: buildConversionSlug(from, to),
        from,
        to,
      });
    }
  }

  return routes.sort((a, b) => a.slug.localeCompare(b.slug));
}

export function getConversionTitle(from: ImageFormat, to: ImageFormat): string {
  return `Convert ${FORMAT_LABELS[from]} to ${FORMAT_LABELS[to]} Online — Free`;
}

export function getConversionDescription(from: ImageFormat, to: ImageFormat): string {
  return `Convert ${FORMAT_LABELS[from]} images to ${FORMAT_LABELS[to]} online with format-specific tips, examples, FAQs, and troubleshooting. Free, fast ${FORMAT_LABELS[from]} to ${FORMAT_LABELS[to]} conversion with no signup.`;
}

export function acceptAttributeForFormat(format: ImageFormat): string {
  const exts = FORMAT_EXTENSIONS[format].map((e) => `.${e}`).join(',');
  return `${FORMAT_MIME[format]},${exts}`;
}
